import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Tooltip, CircularProgress, Card, CardContent,
  Tabs, Tab, TextField, Snackbar, Alert, Avatar
} from '@mui/material';
import {
  ArrowLeft, RefreshCw, IndianRupee, Users, Gift, Landmark,
  CheckCircle2, XCircle, Clock, BadgeCheck
} from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';

const ADMIN_API = `${API_BASE_URL}/api/nextgen-builder/admin`;

const inr = (v) => `₹${(parseFloat(v) || 0).toLocaleString('en-IN')}`;

const StatCard = ({ icon, label, value, sub, color = '#843FEF' }) => (
  <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', flex: 1 }}>
    <CardContent sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ p: 1.2, borderRadius: '12px', bgcolor: `${color}18`, color, display: 'flex' }}>
        {icon}
      </Box>
      <Box>
        <Typography variant="caption" color="#94a3b8" fontWeight="700" sx={{ textTransform: 'uppercase' }}>{label}</Typography>
        <Typography variant="h6" fontWeight="800" color="#1e293b" sx={{ lineHeight: 1.2 }}>{value}</Typography>
        {sub && <Typography variant="caption" color="#64748b">{sub}</Typography>}
      </Box>
    </CardContent>
  </Card>
);

const StatusChip = ({ status }) => {
  const map = {
    success: { bg: '#dcfce7', fg: '#166534' },
    successful: { bg: '#dcfce7', fg: '#166534' },
    completed: { bg: '#dcfce7', fg: '#166534' },
    approved: { bg: '#e0f2fe', fg: '#075985' },
    processing: { bg: '#e0f2fe', fg: '#075985' },
    pending: { bg: '#fef9c3', fg: '#854d0e' },
    rejected: { bg: '#fef2f2', fg: '#991b1b' },
    failed: { bg: '#fef2f2', fg: '#991b1b' },
    cancelled: { bg: '#f1f5f9', fg: '#64748b' }
  };
  const c = map[status] || { bg: '#f1f5f9', fg: '#64748b' };
  return <Chip label={status || 'unknown'} size="small" sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 700, textTransform: 'capitalize', borderRadius: '6px' }} />;
};

const headSx = { fontWeight: '600', color: '#64748b', textTransform: 'uppercase', fontSize: '0.72rem' };

const NextGenFinance = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({});
  const [referrals, setReferrals] = useState([]);
  const [topReferrers, setTopReferrers] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [actionDialog, setActionDialog] = useState(null); // { withdrawal, nextStatus }
  const [remarks, setRemarks] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const get = async (url) => {
      try {
        const res = await axios.get(url);
        return res.data.success ? res.data.data : null;
      } catch (err) {
        console.error(`Fetch failed: ${url}`, err);
        return null;
      }
    };
    const [pay, refs, top, wd] = await Promise.all([
      get(`${ADMIN_API}/payments`),
      get(`${ADMIN_API}/referrals`),
      get(`${ADMIN_API}/top-referrers`),
      get(`${ADMIN_API}/withdrawals`)
    ]);
    if (pay) { setPayments(pay.payments || []); setPaymentSummary(pay.summary || {}); }
    if (refs) setReferrals(refs);
    if (top) setTopReferrers(top);
    if (wd) setWithdrawals(wd);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleWithdrawalAction = async () => {
    if (!actionDialog) return;
    const { withdrawal, nextStatus } = actionDialog;
    try {
      const res = await axios.put(`${ADMIN_API}/withdrawals/${withdrawal.id}/status`, {
        status: nextStatus,
        remarks: remarks || undefined,
        failureReason: nextStatus === 'failed' ? (remarks || 'Marked failed by admin') : undefined
      });
      if (res.data.success) {
        setSnackbar({ open: true, message: res.data.message, severity: 'success' });
        setActionDialog(null);
        setRemarks('');
        fetchAll();
      } else {
        setSnackbar({ open: true, message: res.data.message || 'Update failed', severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Update failed', severity: 'error' });
    }
  };

  const pendingWithdrawals = withdrawals.filter(w => ['pending', 'approved', 'processing'].includes(w.status));
  const pendingAmount = pendingWithdrawals.reduce((s, w) => s + (parseFloat(w.amount) || 0), 0);
  const successfulReferrals = referrals.filter(r => r.referral_status === 'successful').length;

  return (
    <Box p={4} sx={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={() => navigate('/nextgen/projects')} sx={{ color: '#843FEF', bgcolor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', '&:hover': { bgcolor: '#f0eaff' } }}>
            <ArrowLeft size={24} />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
              NextGen Finance
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Payments, referral earnings and payout approvals for NextGen Builder
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<RefreshCw size={18} />} onClick={fetchAll}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 'bold' }}>
          Refresh
        </Button>
      </Box>

      {/* Stat cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <StatCard icon={<IndianRupee size={22} />} label="Total Revenue" value={inr(paymentSummary.total_revenue)}
          sub={`${paymentSummary.successful_payments || 0} successful payments`} color="#16a34a" />
        <StatCard icon={<Gift size={22} />} label="Referral Discounts Given" value={inr(paymentSummary.total_discounts)}
          sub={`${successfulReferrals} successful referrals`} color="#843FEF" />
        <StatCard icon={<Landmark size={22} />} label="Payouts Awaiting Action" value={inr(pendingAmount)}
          sub={`${pendingWithdrawals.length} request(s) pending approval`} color="#d97706" />
        <StatCard icon={<Users size={22} />} label="Referrers" value={topReferrers.filter(r => parseInt(r.total_referrals) > 0).length}
          sub="builders who shared their code" color="#0284c7" />
      </Box>

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ borderBottom: '1px solid #f1f5f9', px: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 700 } }}>
          <Tab label={`Payout Requests (${pendingWithdrawals.length})`} />
          <Tab label="Payment History" />
          <Tab label="Referrals" />
          <Tab label="Top Referrers" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={8}><CircularProgress sx={{ color: '#843FEF' }} /></Box>
        ) : (
          <>
            {/* ── Payout Requests ─────────────────────────────────────── */}
            {tab === 0 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ ...headSx, pl: 3 }}>Request</TableCell>
                      <TableCell sx={headSx}>Builder</TableCell>
                      <TableCell sx={headSx}>Amount</TableCell>
                      <TableCell sx={headSx}>Payout Method</TableCell>
                      <TableCell sx={headSx}>Wallet Balance</TableCell>
                      <TableCell sx={headSx}>Status</TableCell>
                      <TableCell sx={{ ...headSx, pr: 3 }} align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawals.map(w => (
                      <TableRow key={w.id} hover>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography fontWeight="700" color="#1e293b">{w.request_number}</Typography>
                          <Typography variant="caption" color="#94a3b8">{new Date(w.requested_at).toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="600" color="#1e293b">{w.full_name}</Typography>
                          <Typography variant="caption" color="#64748b">{w.email}</Typography>
                        </TableCell>
                        <TableCell><Typography fontWeight="800" color="#1e293b">{inr(w.amount)}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="600" color="#475569">{w.payout_method}</Typography>
                          <Typography variant="caption" color="#94a3b8">{w.upi_id || w.bank_name || ''}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2" color="#475569">{inr(w.current_wallet_balance)}</Typography></TableCell>
                        <TableCell>
                          <StatusChip status={w.status} />
                          {w.remarks && <Typography variant="caption" display="block" color="#94a3b8">{w.remarks}</Typography>}
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}>
                          <Box display="flex" justifyContent="flex-end" gap={0.5}>
                            {w.status === 'pending' && (
                              <>
                                <Tooltip title="Approve — payout is marked completed and the amount moves out of the builder's pending balance">
                                  <IconButton size="small" sx={{ color: '#16a34a' }} onClick={() => setActionDialog({ withdrawal: w, nextStatus: 'completed' })}>
                                    <CheckCircle2 size={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject (amount returns to wallet)">
                                  <IconButton size="small" color="error" onClick={() => setActionDialog({ withdrawal: w, nextStatus: 'rejected' })}>
                                    <XCircle size={18} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {['approved', 'processing'].includes(w.status) && (
                              <>
                                <Tooltip title="Mark paid — money sent to the builder's account">
                                  <IconButton size="small" sx={{ color: '#16a34a' }} onClick={() => setActionDialog({ withdrawal: w, nextStatus: 'completed' })}>
                                    <BadgeCheck size={18} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Mark failed (amount returns to wallet)">
                                  <IconButton size="small" color="error" onClick={() => setActionDialog({ withdrawal: w, nextStatus: 'failed' })}>
                                    <XCircle size={18} />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {['completed', 'rejected', 'failed', 'cancelled'].includes(w.status) && (
                              <Clock size={16} color="#cbd5e1" />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {withdrawals.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Typography color="#94a3b8" fontWeight="600">No payout requests yet</Typography>
                        <Typography variant="caption" color="#cbd5e1">Requests appear here when builders redeem their referral earnings</Typography>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Payment History ─────────────────────────────────────── */}
            {tab === 1 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ ...headSx, pl: 3 }}>Builder</TableCell>
                      <TableCell sx={headSx}>Plan</TableCell>
                      <TableCell sx={headSx}>Amount</TableCell>
                      <TableCell sx={headSx}>Discount</TableCell>
                      <TableCell sx={headSx}>Referral Code Used</TableCell>
                      <TableCell sx={headSx}>Status</TableCell>
                      <TableCell sx={{ ...headSx, pr: 3 }} align="right">Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payments.map(p => (
                      <TableRow key={p.id} hover>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography fontWeight="600" color="#1e293b">{p.full_name || 'Unknown'}</Typography>
                          <Typography variant="caption" color="#64748b">{p.email}</Typography>
                        </TableCell>
                        <TableCell><Chip label={p.subscription_type} size="small" sx={{ fontWeight: 700, textTransform: 'capitalize', bgcolor: '#eef2ff', color: '#4f46e5', borderRadius: '6px' }} /></TableCell>
                        <TableCell>
                          <Typography fontWeight="700" color="#1e293b">{inr(p.amount)}</Typography>
                          {parseFloat(p.discount_amount) > 0 && <Typography variant="caption" color="#94a3b8" sx={{ textDecoration: 'line-through' }}>{inr(p.original_amount)}</Typography>}
                        </TableCell>
                        <TableCell><Typography variant="body2" color={parseFloat(p.discount_amount) > 0 ? '#16a34a' : '#94a3b8'}>{parseFloat(p.discount_amount) > 0 ? `- ${inr(p.discount_amount)}` : '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" fontWeight="600" color="#475569">{p.referral_code_used || '—'}</Typography></TableCell>
                        <TableCell><StatusChip status={p.payment_status} /></TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}><Typography variant="caption" color="#64748b">{new Date(p.created_at).toLocaleString()}</Typography></TableCell>
                      </TableRow>
                    ))}
                    {payments.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8 }}><Typography color="#94a3b8" fontWeight="600">No payments yet</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Referrals ───────────────────────────────────────────── */}
            {tab === 2 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ ...headSx, pl: 3 }}>Referrer</TableCell>
                      <TableCell sx={headSx}>Referred (joined with code)</TableCell>
                      <TableCell sx={headSx}>Status</TableCell>
                      <TableCell sx={headSx}>Reward Credited</TableCell>
                      <TableCell sx={headSx}>Discount Given</TableCell>
                      <TableCell sx={headSx}>Redeemed</TableCell>
                      <TableCell sx={{ ...headSx, pr: 3 }} align="right">Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referrals.map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ pl: 3 }}>
                          <Typography fontWeight="600" color="#1e293b">{r.referrer_name}</Typography>
                          <Typography variant="caption" color="#64748b">{r.referrer_email}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontWeight="600" color="#1e293b">{r.referred_name}</Typography>
                          <Typography variant="caption" color="#64748b">{r.referred_email}</Typography>
                        </TableCell>
                        <TableCell><StatusChip status={r.referral_status} /></TableCell>
                        <TableCell><Typography fontWeight="700" color={parseFloat(r.wallet_credit) > 0 ? '#16a34a' : '#94a3b8'}>{parseFloat(r.wallet_credit) > 0 ? inr(r.wallet_credit) : '—'}</Typography></TableCell>
                        <TableCell><Typography variant="body2" color="#475569">{parseFloat(r.discount_given) > 0 ? inr(r.discount_given) : '—'}</Typography></TableCell>
                        <TableCell>
                          <Chip
                            label={parseFloat(r.wallet_credit) > 0 ? 'Credited to wallet' : 'Not yet'}
                            size="small"
                            sx={{
                              fontWeight: 700, borderRadius: '6px',
                              bgcolor: parseFloat(r.wallet_credit) > 0 ? '#dcfce7' : '#f1f5f9',
                              color: parseFloat(r.wallet_credit) > 0 ? '#166534' : '#64748b'
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ pr: 3 }}><Typography variant="caption" color="#64748b">{new Date(r.created_at).toLocaleDateString()}</Typography></TableCell>
                      </TableRow>
                    ))}
                    {referrals.length === 0 && (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 8 }}><Typography color="#94a3b8" fontWeight="600">No referrals yet</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* ── Top Referrers ───────────────────────────────────────── */}
            {tab === 3 && (
              <TableContainer>
                <Table>
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ ...headSx, pl: 3 }}>Builder</TableCell>
                      <TableCell sx={headSx} align="center">Total Referrals</TableCell>
                      <TableCell sx={headSx} align="center">Successful</TableCell>
                      <TableCell sx={headSx}>Total Earnings</TableCell>
                      <TableCell sx={{ ...headSx, pr: 3 }}>Current Wallet Balance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topReferrers.filter(r => parseInt(r.total_referrals) > 0).map(r => (
                      <TableRow key={r.id} hover>
                        <TableCell sx={{ pl: 3 }}>
                          <Box display="flex" alignItems="center" gap={1.5}>
                            <Avatar sx={{ bgcolor: '#843FEF', width: 34, height: 34, fontSize: '0.9rem' }}>{r.full_name?.charAt(0)}</Avatar>
                            <Box>
                              <Typography fontWeight="600" color="#1e293b">{r.full_name}</Typography>
                              <Typography variant="caption" color="#64748b">{r.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center"><Typography fontWeight="700">{r.total_referrals}</Typography></TableCell>
                        <TableCell align="center"><Typography fontWeight="700" color="#16a34a">{r.successful_referrals}</Typography></TableCell>
                        <TableCell><Typography fontWeight="700" color="#1e293b">{inr(r.total_earnings)}</Typography></TableCell>
                        <TableCell sx={{ pr: 3 }}><Typography fontWeight="600" color="#475569">{inr(r.current_balance)}</Typography></TableCell>
                      </TableRow>
                    ))}
                    {topReferrers.filter(r => parseInt(r.total_referrals) > 0).length === 0 && (
                      <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8 }}><Typography color="#94a3b8" fontWeight="600">No referrers yet</Typography></TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>

      {/* Approve / Reject / Complete confirmation dialog */}
      <Dialog open={!!actionDialog} onClose={() => { setActionDialog(null); setRemarks(''); }} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {actionDialog?.nextStatus === 'completed' && 'Approve & complete payout'}
          {actionDialog?.nextStatus === 'rejected' && 'Reject payout request'}
          {actionDialog?.nextStatus === 'failed' && 'Mark payout as failed'}
        </DialogTitle>
        <DialogContent dividers>
          {actionDialog && (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="#475569">
                {actionDialog.withdrawal.full_name} — <b>{inr(actionDialog.withdrawal.amount)}</b> via {actionDialog.withdrawal.payout_method}
                {actionDialog.withdrawal.upi_id ? ` (${actionDialog.withdrawal.upi_id})` : ''}
              </Typography>
              {actionDialog.nextStatus === 'completed' && (
                <Alert severity="info" sx={{ borderRadius: '10px' }}>
                  Approving completes the payout: transfer the money to the builder, then confirm. The amount moves out of their pending balance permanently and their history shows "Completed".
                </Alert>
              )}
              {['rejected', 'failed'].includes(actionDialog.nextStatus) && (
                <Alert severity="warning" sx={{ borderRadius: '10px' }}>
                  The amount will be returned to the builder's wallet balance.
                </Alert>
              )}
              <TextField
                label="Remarks (visible to the builder)"
                fullWidth multiline rows={2}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setActionDialog(null); setRemarks(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleWithdrawalAction}
            color={['rejected', 'failed'].includes(actionDialog?.nextStatus) ? 'error' : 'primary'}
            sx={{ fontWeight: 700, ...(actionDialog?.nextStatus === 'completed' ? { bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } } : {}) }}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NextGenFinance;
